import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  type ReactNode,
  type MouseEvent,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Modal content */
  children: ReactNode;
  /** Modal title for accessibility */
  ariaLabel?: string;
  /** ID for aria-labelledby (if using a visible title) */
  ariaLabelledBy?: string;
  /** Whether clicking the backdrop closes the modal (default: true) */
  closeOnBackdropClick?: boolean;
  /** Whether pressing Escape closes the modal (default: true) */
  closeOnEscape?: boolean;
  /** Whether to center the modal vertically (default: true) */
  centered?: boolean;
  /** Modal size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
  /** Additional class name for the modal dialog */
  className?: string;
  /** Portal container selector (default: '#modals') */
  portalContainer?: string;
  /** Animation duration in ms (default: 200) */
  animationDuration?: number;
}

/**
 * Modal - A React modal component that replaces Bootstrap modals
 *
 * Features:
 * - Uses React portals for proper stacking
 * - Accessible with proper ARIA attributes
 * - Focus trapping within modal
 * - Body scroll lock when open
 * - Keyboard support (Escape to close)
 * - Smooth enter/exit animations
 * - Customizable backdrop and keyboard behavior
 */
export function Modal({
  isOpen,
  onClose,
  children,
  ariaLabel,
  ariaLabelledBy,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  centered = true,
  size = 'md',
  className = '',
  portalContainer = '#modals',
  animationDuration = 200,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;
      setIsVisible(true);
      // Trigger enter animation on next frame
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else if (isVisible) {
      // Trigger exit animation
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        // Restore focus to previously focused element
        previousActiveElement.current?.focus();
      }, animationDuration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, animationDuration, isVisible]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isVisible]);

  // Focus the modal when it opens
  useEffect(() => {
    if (isAnimating && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isAnimating]);

  // Handle Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      // Only close if clicking directly on the backdrop (not modal content)
      if (event.target === event.currentTarget && closeOnBackdropClick) {
        onClose();
      }
    },
    [closeOnBackdropClick, onClose]
  );

  // Prevent clicks inside modal from bubbling to backdrop
  const handleModalClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  // Don't render anything if not visible
  if (!isVisible) {
    return null;
  }

  // Get the portal container
  const container = document.querySelector(portalContainer);
  if (!container) {
    console.warn(`Modal: Portal container "${portalContainer}" not found`);
    return null;
  }

  // Size classes
  const sizeClass = size === 'md' ? '' : `modal-${size}`;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className={`modal-backdrop-custom ${isAnimating ? 'show' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1050,
          opacity: isAnimating ? 1 : 0,
          transition: `opacity ${animationDuration}ms ease-in-out`,
        }}
        aria-hidden="true"
      />

      {/* Modal container */}
      <div
        ref={modalRef}
        className={`modal-custom ${isAnimating ? 'show' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1055,
          overflow: 'auto',
          outline: 'none',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={handleBackdropClick}
      >
        <div
          className={`modal-dialog ${centered ? 'modal-dialog-centered' : ''} ${sizeClass} ${className}`}
          style={{
            position: 'relative',
            margin: centered ? 'auto' : '1.75rem auto',
            maxWidth: size === 'sm' ? '300px' : size === 'lg' ? '800px' : size === 'xl' ? '1140px' : '500px',
            minHeight: centered ? '100%' : 'auto',
            display: centered ? 'flex' : 'block',
            alignItems: centered ? 'center' : 'flex-start',
            padding: '1rem',
            opacity: isAnimating ? 1 : 0,
            transform: isAnimating ? 'translateY(0)' : 'translateY(-20px)',
            transition: `opacity ${animationDuration}ms ease-in-out, transform ${animationDuration}ms ease-in-out`,
          }}
          onClick={handleModalClick}
        >
          <div
            className="modal-content"
            style={{
              width: '100%',
              backgroundColor: '#fff',
              borderRadius: '0.5rem',
              boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, container);
}

/**
 * ModalHeader - Header section of the modal
 */
export interface ModalHeaderProps {
  children: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  className?: string;
}

export function ModalHeader({ children, onClose, closeLabel = 'Close', className = '' }: ModalHeaderProps) {
  return (
    <div className={`modal-header ${className}`} style={{ display: 'flex', alignItems: 'center', padding: '1rem', borderBottom: '1px solid #dee2e6' }}>
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button
          type="button"
          className="btn-close"
          aria-label={closeLabel}
          onClick={onClose}
          style={{
            background: 'transparent url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\' fill=\'%23000\'%3e%3cpath d=\'M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z\'/%3e%3c/svg%3e") center/1em auto no-repeat',
            border: 0,
            borderRadius: '0.375rem',
            width: '1.5rem',
            height: '1.5rem',
            opacity: 0.5,
            cursor: 'pointer',
          }}
        />
      )}
    </div>
  );
}

/**
 * ModalBody - Body section of the modal
 */
export interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export function ModalBody({ children, className = '' }: ModalBodyProps) {
  return (
    <div className={`modal-body ${className}`} style={{ padding: '1rem' }}>
      {children}
    </div>
  );
}

/**
 * ModalFooter - Footer section of the modal
 */
export interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export function ModalFooter({ children, className = '' }: ModalFooterProps) {
  return (
    <div
      className={`modal-footer ${className}`}
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.5rem',
        padding: '1rem',
        borderTop: '1px solid #dee2e6',
      }}
    >
      {children}
    </div>
  );
}

export default Modal;
